import { useContext, useMemo, useState, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import AccionaFormItem from '../../../../../components/AccionaFormItem';
import AccionaInput from '../../../../../components/AccionaInput';
import style from './index.module.scss';
import AccionaButton from '../../../../../components/AccionaButton';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { MainContext } from '../../../../../context/MainContext';
import AccionaAlert from '../../../../../components/AccionaAlert';
import AccionaSelect from '../../../../../components/AccionaSelect';
import Monitoring from '../../../../../models/hoodedControl/Monitoring';
import SensorsFormField from './components/SensorsFormField';
import { getUnusedDevices } from '../../../../../services/redux/hoodedControl/devices/actions';
import AccionaConfirm from '../../../../../components/AccionaConfirm';
import { areObjectsEqual } from '../../../../../utils/utils';

const { wrapper, footer, sensors, main, showAddButton, hiddenZone, visible } =
  style;

const CreateMonitoring = ({
  initialData,
  onFinish,
  onCancel,
  onFinishComplete,
}) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { project, setLoading: setMainLoading } = useContext(MainContext);
  const { errorCreated } = useSelector(
    ({ hoodedControlMonitorings }) => hoodedControlMonitorings,
  );
  const { unusedList, fetching: loading } = useSelector(
    ({ hoodedControlDevices }) => hoodedControlDevices,
  );
  const [monitoring, setMonitoring] = useState(
    initialData || new Monitoring({ project: project.id_project }),
  );
  const [isMonitoringIdValidated, setIsMonitoringIdValidated] = useState(
    initialData || false,
  );
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const hasTheMonitoringChange = useMemo(() => {
    return initialData && !areObjectsEqual(initialData, monitoring);
  }, [initialData, monitoring]);

  const onAddClick = () => {
    const parsedMonitoring = new Monitoring({
      ...monitoring,
    });
    [...Array(7)].forEach((_, index) => {
      const channel = monitoring[`ch${index + 1}`];
      if (channel && channel.enabled === undefined) {
        channel.enabled = false;
      }
    });
    onFinish(parsedMonitoring);
  };

  const onFinishClick = () => {
    setShowConfirmModal({
      message: intl.formatMessage({
        id: 'strength.hoodedcontrol.monitorings.create.finishMonitoring.message',
      }),
      onConfirm: () => {
        onFinishComplete(monitoring);
        setShowConfirmModal();
      },
      onCancel: () => {
        setShowConfirmModal();
      },
    });
  };

  const validationError = useMemo(() => {
    let e = false;
    if (
      !monitoring.description ||
      !monitoring.device ||
      !monitoring.maturity_method
    ) {
      return true;
    }
    [...Array(7)].forEach((_, index) => {
      const channel = monitoring[`ch${index + 1}`];
      if (channel?.enabled && !channel?.ts) {
        e = true;
      }
    });
    return e;
  }, [monitoring]);

  useEffect(() => {
    if (errorCreated) {
      setMainLoading(false);
    }
  }, [setMainLoading, errorCreated]);

  useEffect(() => {
    dispatch(getUnusedDevices());
  }, [dispatch]);

  return (
    <div className={wrapper}>
      <div className={`${main} ${isMonitoringIdValidated ? visible : ''}`}>
        <div>
          <AccionaFormItem>
            <AccionaInput
              state={{ value: monitoring.description, status: [] }}
              label="description"
              onChange={(label, description) => {
                setMonitoring((prev) => ({ ...prev, description }));
              }}
            />
          </AccionaFormItem>
          <AccionaFormItem>
            <AccionaSelect
              state={{
                value: monitoring.device,
                status: [],
                rules: { required: true },
              }}
              loading={loading}
              disabled={!unusedList.length && !loading}
              defaultValue={
                unusedList.length || monitoring.device
                  ? {
                      label: monitoring.device_data?.reference_number,
                      id: monitoring.device,
                    }
                  : {
                      label: `-- ${intl.formatMessage({
                        id: 'noUnusedDevices',
                      })} --`,
                    }
              }
              value={monitoring.device}
              label="device"
              onChange={(device) => {
                setMonitoring((prev) => ({ ...prev, device }));
                setIsMonitoringIdValidated(true);
              }}
              options={
                unusedList?.map((device) => ({
                  label: device.reference_number,
                  value: device.id,
                })) || []
              }
            />
          </AccionaFormItem>
        </div>
        <div
          className={`${hiddenZone} ${isMonitoringIdValidated ? visible : ''}`}
        >
          <AccionaFormItem>
            <AccionaSelect
              state={{
                value: monitoring.maturity_method,
                status: [],
                rules: { required: true },
              }}
              value={monitoring.maturity_method}
              label="maturity_method"
              onChange={(maturity_method) => {
                setMonitoring((prev) => ({ ...prev, maturity_method }));
              }}
              options={[
                {
                  label: intl.formatMessage({ id: 'maturityMethod.NurseSaul' }),
                  value: 'NurseSaul',
                },
                {
                  label: intl.formatMessage({ id: 'maturityMethod.Arrhenius' }),
                  value: 'Arrhenius',
                },
              ]}
            />
          </AccionaFormItem>
          <div className={sensors}>
            <p>
              {intl.formatMessage({
                id: 'strength.hoodedcontrol.monitorings.create.sensors',
              })}
            </p>
            <SensorsFormField value={monitoring} onChange={setMonitoring} />
          </div>
        </div>
        {errorCreated && (
          <AccionaAlert
            message={intl.formatMessage({
              id: 'strength.hoodedcontrol.monitorings.create.error',
            })}
          />
        )}
      </div>
      <div
        className={`${footer} ${isMonitoringIdValidated ? showAddButton : ''}`}
      >
        <AccionaButton
          type="default"
          msg={intl.formatMessage({ id: 'cancel' })}
          onClick={onCancel}
        />
        <AccionaButton
          msg={intl.formatMessage({
            id: 'button.create.end',
          })}
          onClick={onFinishClick}
          disabled={
            Boolean(validationError) || !monitoring.id || hasTheMonitoringChange
          }
        />
        {isMonitoringIdValidated && (
          <AccionaButton
            msg={intl.formatMessage({
              id: initialData ? 'button.update.end' : 'button.add',
            })}
            disabled={Boolean(validationError)}
            onClick={onAddClick}
          />
        )}
      </div>
      {showConfirmModal && (
        <AccionaConfirm
          message={showConfirmModal.message}
          onCancel={showConfirmModal.onCancel}
          onConfirm={showConfirmModal.onConfirm}
        />
      )}
    </div>
  );
};

CreateMonitoring.propTypes = {
  initialData: PropTypes.instanceOf(Monitoring),
  onFinish: PropTypes.func,
  onCancel: PropTypes.func,
  onFinishComplete: PropTypes.func,
};

CreateMonitoring.defaultProps = {
  initialData: undefined,
  onFinish: () => {},
  onCancel: () => {},
  onFinishComplete: () => {},
};

export default CreateMonitoring;
